import { useEffect, useState, useSyncExternalStore } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { Link, useLocation } from "react-router-dom";
import { useStore } from "react-admin";
import { authStore } from "../backoffice/client";
import { useBrandScope } from "../backoffice/shared";
import { DroneIcon } from "./DroneIcon";

const backofficePages = [
  { label: "Transactions", path: "/transactions" },
  { label: "Sessions", path: "/sessions" },
  { label: "Players", path: "/players" },
  { label: "Games", path: "/games" },
  { label: "Users", path: "/users" },
];

const aviadroneSections = [
  {
    label: "Cache",
    prefix: "/crash/fdb",
    items: [
      { label: "Overview", path: "/crash/fdb" },
      { label: "Game Configs", path: "/crash/fdb/game-configs" },
    ],
  },
  {
    label: "Records",
    prefix: "/crash/db-",
    items: [
      { label: "Rounds", path: "/crash/db-rounds" },
      { label: "Bets", path: "/crash/db-bets" },
      { label: "Wallet Requests", path: "/crash/db-wallet-requests" },
      { label: "Users", path: "/crash/db-users" },
      { label: "Operators", path: "/crash/db-operators" },
      { label: "Game Configs", path: "/crash/db-game-configs" },
    ],
  },
  {
    label: "Reports",
    prefix: "/crash/operator-stats",
    items: [{ label: "Operator Stats", path: "/crash/operator-stats" }],
  },
  {
    label: "Integrity",
    prefix: "/crash/recon",
    items: [{ label: "Reconciliation", path: "/crash/recon" }],
  },
];

function isAviadrone(pathname: string) {
  return pathname === "/crash" || pathname.startsWith("/crash/");
}

function matchesPage(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function ModuleMenu({
  onNavigate,
  compact = false,
  onToggle,
}: {
  onNavigate?: () => void;
  compact?: boolean;
  onToggle?: () => void;
}) {
  const { pathname } = useLocation();
  const { brandId } = useBrandScope();
  const modules = [
    {
      label: "Backoffice",
      description: "Platform operations",
      path: `/transactions${brandId === undefined ? "" : `?brandId=${brandId}`}`,
      selected: backofficePages.some((page) =>
        matchesPage(pathname, page.path),
      ),
      icon: <DashboardOutlinedIcon />,
    },
    {
      label: "Aviadrone",
      description: "Crash game operations",
      path: "/crash/fdb",
      selected: isAviadrone(pathname),
      icon: <DroneIcon />,
    },
  ];
  return (
    <Box component="nav" aria-label="Modules">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={compact ? "center" : "space-between"}
        sx={{ minHeight: 40, mb: 1, pl: compact ? 0 : 1.5 }}
      >
        {!compact && (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 1.5 }}
          >
            Modules
          </Typography>
        )}
        {onToggle && (
          <Tooltip
            title={compact ? "Expand navigation" : "Collapse navigation"}
          >
            <IconButton
              size="small"
              aria-label={compact ? "Expand navigation" : "Collapse navigation"}
              aria-expanded={!compact}
              onClick={onToggle}
            >
              {compact ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <List disablePadding sx={{ display: "grid", gap: 1 }}>
        {modules.map((module) => (
          <Tooltip
            key={module.label}
            title={compact ? module.label : ""}
            placement="right"
          >
            <ListItemButton
              component={Link}
              to={module.path}
              aria-label={module.label}
              selected={module.selected}
              aria-current={module.selected ? "page" : undefined}
              onClick={onNavigate}
              sx={{
                borderRadius: 1,
                alignItems: compact ? "center" : "flex-start",
                justifyContent: compact ? "center" : "flex-start",
                px: compact ? 1 : 1.5,
                py: 1,
                minHeight: 48,
                "&.Mui-selected": {
                  bgcolor: "rgba(124,58,237,0.16)",
                  boxShadow: "inset 3px 0 0 #a78bfa",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: compact ? 0 : 36,
                  mt: compact ? 0 : 0.75,
                  color: module.selected ? "primary.light" : "text.secondary",
                }}
              >
                {module.icon}
              </ListItemIcon>
              {!compact && (
                <ListItemText
                  primary={module.label}
                  secondary={module.description}
                  slotProps={{
                    primary: { fontWeight: module.selected ? 700 : 500 },
                    secondary: { sx: { mt: 0.5, fontSize: "0.75rem" } },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );
}

export function ModuleSidebar() {
  const [expanded, setExpanded] = useStore<boolean>(
    "navigation.sidebarExpanded",
    true,
  );
  return (
    <Box
      component="aside"
      aria-label="Module sidebar"
      sx={{
        display: { xs: "none", md: "block" },
        width: expanded ? 232 : 72,
        flexShrink: 0,
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        transition: (theme) =>
          theme.transitions.create("width", { duration: 180 }),
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box sx={{ position: "sticky", top: 16, p: expanded ? 2 : 1 }}>
        <ModuleMenu
          compact={!expanded}
          onToggle={() => setExpanded(!expanded)}
        />
      </Box>
    </Box>
  );
}

export function MobileModuleMenu() {
  const [open, setOpen] = useState(false);
  const mobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("md"));
  useEffect(() => {
    if (!mobile) setOpen(false);
  }, [mobile]);
  const close = () => setOpen(false);
  return (
    <>
      <Button
        color="inherit"
        startIcon={<MenuOpenIcon />}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open && mobile}
        aria-controls={open && mobile ? "mobile-module-drawer" : undefined}
        sx={{ display: { xs: "inline-flex", md: "none" } }}
      >
        Modules
      </Button>
      <Drawer
        anchor="left"
        open={open && mobile}
        onClose={close}
        sx={{ display: { xs: "block", md: "none" } }}
        slotProps={{
          paper: {
            id: "mobile-module-drawer",
            role: "dialog",
            "aria-label": "Modules",
            "aria-modal": true,
            sx: { width: 280, maxWidth: "calc(100vw - 32px)", p: 2 },
          },
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <IconButton aria-label="Close modules" onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <ModuleMenu onNavigate={close} />
      </Drawer>
    </>
  );
}

export function SectionNavigation() {
  const { pathname } = useLocation();
  const { brandId } = useBrandScope();
  const auth = useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
  if (isAviadrone(pathname)) {
    const index = Math.max(
      0,
      aviadroneSections.findIndex((section) =>
        pathname.startsWith(section.prefix),
      ),
    );
    const section = aviadroneSections[index];
    const active =
      section.items.find((item) =>
        item.path === "/crash/fdb"
          ? pathname === item.path || pathname.startsWith("/crash/fdb/rounds/")
          : matchesPage(pathname, item.path),
      )?.path ?? false;
    return (
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Aviadrone
        </Typography>
        <Tabs
          value={index}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Aviadrone sections"
        >
          {aviadroneSections.map((entry, i) => (
            <Tab
              key={entry.label}
              label={entry.label}
              value={i}
              component={Link}
              to={entry.items[0].path}
            />
          ))}
        </Tabs>
        {section.items.length > 1 && (
          <Tabs
            value={active}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Aviadrone pages"
          >
            {section.items.map((item) => (
              <Tab
                key={item.path}
                label={item.label}
                value={item.path}
                component={Link}
                to={item.path}
              />
            ))}
          </Tabs>
        )}
      </Box>
    );
  }
  const visible = backofficePages.filter(
    (page) => page.path !== "/users" || auth.user?.role !== "BRAND_VIEWER",
  );
  const active = visible.find((page) => matchesPage(pathname, page.path))?.path;
  if (!active) return null;
  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
      <Typography variant="overline" color="text.secondary">
        Backoffice
      </Typography>
      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Backoffice sections"
      >
        {visible.map((page) => (
          <Tab
            key={page.path}
            label={page.label}
            value={page.path}
            component={Link}
            to={`${page.path}${brandId === undefined ? "" : `?brandId=${brandId}`}`}
          />
        ))}
      </Tabs>
    </Box>
  );
}
